import dayjs from 'dayjs';
import { ArrowLeft, Download } from 'lucide-react';
import { toast } from 'sonner';

import {
  ExportPostsInBoard,
  POSTS_PER_BOARD_LIMIT,
  useRemovePostsFromBoard,
} from '@/entities/board';
import type { PostListItem } from '@/entities/post/post-types';
import { SocialPostsGrid } from '@/features/(insight)/posts/ui/social-post-card';
import { Button } from '@/shared/components/shadcn/button';
import { DATE_FORMATS } from '@/shared/utils/date/date-formats';
import { exportToCSV } from '@/shared/utils/export-to-csv';
import { requestConfirm } from '@/shared/utils/request-confirm';

type Props = {
  boardId: string;
  title: string;
  updatedAt: DateISOString;
  postCount: number;
  posts: PostListItem[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  onBack: () => void;
};

export function BoardDetailSection({
  boardId,
  title,
  updatedAt,
  postCount,
  posts,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore,
  onBack,
}: Props) {
  const removePostsFromBoardMutation = useRemovePostsFromBoard();

  const handleExportCsv = async () => {
    const text = await ExportPostsInBoard(boardId);
    exportToCSV(text, title);
  };

  const handleRemoveSinglePost = async (postId: string) => {
    await requestConfirm({
      title: 'Remove post from board?',
      description: 'Are you sure you want to remove this post from this board?',
      confirmButton: {
        children: 'Remove',
        variant: 'destructive',
      },
      onConfirm: async () => {
        await removePostsFromBoardMutation.mutateAsync({ boardId, postIds: [postId] });
        toast.success('Removed from board');
      },
    });
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-border bg-background border-b px-8 py-6">
        <div className="mb-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="mr-2 size-4" />
            Back to Boards
          </Button>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-foreground mb-1 text-2xl font-bold">{title}</h1>
            <p className="text-muted-foreground text-sm">
              {postCount}/{POSTS_PER_BOARD_LIMIT} posts · Updated{' '}
              {dayjs(updatedAt).format(DATE_FORMATS.base)}
            </p>
          </div>
          <Button onClick={handleExportCsv}>
            <Download className="mr-2 size-4" />
            Export CSV
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <SocialPostsGrid
          posts={posts}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          onLoadMore={onLoadMore}
          className="p-0"
          onRemovePost={handleRemoveSinglePost}
          removeTooltip="Remove from board"
        />
      </div>
    </div>
  );
}
